import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiAlertTriangle,
  FiArrowDown,
  FiArrowUp,
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiUser,
  FiMapPin,
  FiX,
} from 'react-icons/fi';
import { alarmService } from '../../services/alarmService';
import type { Alarm } from '../../services/alarmService';
import { supabase } from '../../supabaseClient';
import { getLocale, t, tn, useI18n } from '../../i18n';
import './AlarmsPage.css';

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return t('N/A');
  const diff = Date.now() - new Date(ts).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return t('{count}s ago', { count: secs });
  const mins = Math.floor(secs / 60);
  if (mins < 60) return t('{count}m ago', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('{count}h ago', { count: hrs });
  return new Date(ts).toLocaleDateString(getLocale());
}

function formatDatetime(ts: string | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(getLocale(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return '—';
  const mins = Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

const statusConfig = {
  open: { label: 'Open||alarm', chipCls: 'status-chip--error' },
  acknowledged: { label: 'Acknowledged||alarm', chipCls: 'status-chip--warning' },
  resolved: { label: 'Resolved||alarm', chipCls: 'status-chip--success' },
  cancelled: { label: 'Cancelled||alarm', chipCls: 'status-chip--neutral' },
};

function StatusChip({ status }: { status: string }) {
  const cfg = statusConfig[status as keyof typeof statusConfig] ?? {
    label: status,
    chipCls: 'status-chip--neutral',
  };
  return <span className={`status-chip ${cfg.chipCls}`}>{t(cfg.label)}</span>;
}

type SortCol = 'status' | 'title' | 'guard' | 'site' | 'triggered' | 'duration';

function SortableHeader({
  col,
  label,
  active,
  dir,
  onSort,
}: {
  col: SortCol;
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: (col: SortCol) => void;
}) {
  return (
    <th
      className={`alarms-th-sortable ${active ? 'alarms-th-sortable--active' : ''}`}
      onClick={() => onSort(col)}
    >
      <span className="alarms-th-inner">
        {t(label)}
        <span className="alarms-th-sort-icon">
          {active ? (
            dir === 'asc' ? <FiArrowUp /> : <FiArrowDown />
          ) : (
            <span className="alarms-th-sort-neutral">↕</span>
          )}
        </span>
      </span>
    </th>
  );
}

interface AlarmRowProps {
  alarm: Alarm;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  actionLoading: string | null;
  /** The alarm the user was sent here to look at. */
  isFocused: boolean;
}

function AlarmRow({ alarm, onAcknowledge, onResolve, actionLoading, isFocused }: AlarmRowProps) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const guardName = alarm.profiles?.full_name ?? t('Unknown Guard');
  const siteName = alarm.sites?.name ?? t('Unknown Site');
  const isOpen = alarm.status === 'open';
  const isAcked = alarm.status === 'acknowledged';
  const isWelfareAlarm = alarm.metadata?.source === 'welfare_check';

  useEffect(() => {
    if (isFocused) rowRef.current?.scrollIntoView?.({ block: 'center' });
  }, [isFocused]);

  return (
    <div
      ref={rowRef}
      className={`alarm-row ${isOpen ? 'alarm-row--open' : ''} ${isAcked ? 'alarm-row--acked' : ''} ${isFocused ? 'alarm-row--focused' : ''}`}
      aria-current={isFocused ? 'true' : undefined}
    >
      <div className="alarm-row__strip" />

      <div className="alarm-row__icon-wrap">
        {isOpen ? (
          <FiAlertTriangle className="alarm-row__icon alarm-row__icon--open" />
        ) : (
          <FiClock className="alarm-row__icon alarm-row__icon--acked" />
        )}
      </div>

      <div className="alarm-row__body">
        <div className="alarm-row__title-line">
          <span className="alarm-row__title">{alarm.title ?? t('Alarm')}</span>
          {isWelfareAlarm && (
            <span className="alarm-row__source-chip">{t('Welfare check')}</span>
          )}
          <StatusChip status={alarm.status} />
        </div>
        {alarm.message && (
          <div className="alarm-row__message">{alarm.message}</div>
        )}
        <div className="alarm-row__meta">
          <span className="alarm-row__meta-item">
            <FiUser className="alarm-row__meta-icon" />
            {guardName}
          </span>
          <span className="alarm-row__meta-item">
            <FiMapPin className="alarm-row__meta-icon" />
            {siteName}
          </span>
          <span
            className="alarm-row__meta-item alarm-row__time"
            title={formatDatetime(alarm.triggered_at)}
          >
            <FiClock className="alarm-row__meta-icon" />
            {formatRelative(alarm.triggered_at)}
          </span>
        </div>
      </div>

      <div className="alarm-row__actions">
        {isOpen && (
          <button
            className="btn btn--sm btn--danger"
            onClick={() => onAcknowledge(alarm.id)}
            disabled={actionLoading === alarm.id + '_ack'}
          >
            {actionLoading === alarm.id + '_ack' ? '…' : t('Acknowledge')}
          </button>
        )}
        {(isOpen || isAcked) && (
          <button
            className="btn btn--sm btn--secondary"
            onClick={() => onResolve(alarm.id)}
            disabled={actionLoading === alarm.id + '_res'}
          >
            {actionLoading === alarm.id + '_res' ? '…' : t('Resolve')}
          </button>
        )}
      </div>
    </div>
  );
}

interface Props {
  activeAlarms: Alarm[];
  acknowledgeAlarm: (alarmId: string) => Promise<void>;
  resolveAlarm: (alarmId: string) => Promise<void>;
  tab?: 'active' | 'history';
  onOpenReport?: (reportId: string) => void;
  /** An active alarm to bring into view, e.g. one picked on the live map. */
  navigationTarget?: {
    alarmId: string;
    nonce: number;
  } | null;
  onNavigationHandled?: () => void;
}

export default function AlarmsPage({
  activeAlarms,
  acknowledgeAlarm,
  resolveAlarm,
  tab = 'active',
  onOpenReport,
  navigationTarget = null,
  onNavigationHandled,
}: Props) {
  useI18n();
  const [focusedAlarmId, setFocusedAlarmId] = useState<string | null>(null);
  const handledNavigationNonceRef = useRef<number | null>(null);
  const [history, setHistory] = useState<Alarm[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('all');
  const [sortCol, setSortCol] = useState<SortCol>('triggered');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();
      if ((data as { company_id?: string } | null)?.company_id) {
        setCompanyId((data as { company_id: string }).company_id);
      }
    });
  }, []);

  useEffect(() => {
    if (tab === 'history' && companyId) {
      setHistoryLoading(true);
      alarmService
        .fetchAlarmHistory(companyId)
        .then(setHistory)
        .catch((err: Error) => console.error('Error loading alarm history:', err.message))
        .finally(() => setHistoryLoading(false));
    }
  }, [tab, companyId]);

  useEffect(() => {
    if (!navigationTarget?.alarmId) return;
    if (handledNavigationNonceRef.current === navigationTarget.nonce) return;

    handledNavigationNonceRef.current = navigationTarget.nonce;
    // A search or status filter still set on this page must not hide the target.
    setSearch('');
    setFilterStatus('all');
    setFocusedAlarmId(navigationTarget.alarmId);
    onNavigationHandled?.();
  }, [navigationTarget, onNavigationHandled]);

  const handleAcknowledge = async (alarmId: string) => {
    setActionLoading(alarmId + '_ack');
    await acknowledgeAlarm(alarmId);
    setActionLoading(null);
  };

  const handleResolve = async (alarmId: string) => {
    setActionLoading(alarmId + '_res');
    await resolveAlarm(alarmId);
    setActionLoading(null);
  };

  const openCount = activeAlarms.filter((a) => a.status === 'open').length;
  const ackedCount = activeAlarms.filter((a) => a.status === 'acknowledged').length;
  const totalActive = openCount + ackedCount;

  const filteredActive = useMemo(() => {
    return activeAlarms.filter((a) => {
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (a.title ?? '').toLowerCase().includes(q) ||
          (a.profiles?.full_name ?? '').toLowerCase().includes(q) ||
          (a.sites?.name ?? '').toLowerCase().includes(q) ||
          (a.message ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [activeAlarms, search, filterStatus]);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filteredHistory = useMemo(() => {
    let result = history.filter((a) => {
      if (historyStatusFilter !== 'all' && a.status !== historyStatusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (a.title ?? '').toLowerCase().includes(q) ||
          (a.profiles?.full_name ?? '').toLowerCase().includes(q) ||
          (a.sites?.name ?? '').toLowerCase().includes(q) ||
          (a.message ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });

    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'status':
          cmp = (a.status ?? '').localeCompare(b.status ?? '');
          break;
        case 'title':
          cmp = (a.title ?? '').localeCompare(b.title ?? '');
          break;
        case 'guard':
          cmp = (a.profiles?.full_name ?? '').localeCompare(b.profiles?.full_name ?? '');
          break;
        case 'site':
          cmp = (a.sites?.name ?? '').localeCompare(b.sites?.name ?? '');
          break;
        case 'triggered':
          cmp = new Date(a.triggered_at).getTime() - new Date(b.triggered_at).getTime();
          break;
        case 'duration': {
          const dA = a.resolved_at
            ? new Date(a.resolved_at).getTime() - new Date(a.triggered_at).getTime()
            : 0;
          const dB = b.resolved_at
            ? new Date(b.resolved_at).getTime() - new Date(b.triggered_at).getTime()
            : 0;
          cmp = dA - dB;
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [history, search, historyStatusFilter, sortCol, sortDir]);

  const openAlarms = filteredActive.filter((a) => a.status === 'open');
  const ackedAlarms = filteredActive.filter((a) => a.status === 'acknowledged');
  const alarmRowProps = { onAcknowledge: handleAcknowledge, onResolve: handleResolve, actionLoading };

  return (
    <div className="alarms-page">
      {/* ── Hero ── */}
      <div className="alarms-page__hero">
        <div>
          <div className="page__eyebrow">{t('Security Alerts')}</div>
          <h1 className="alarms-page__title">{t('Alarms')}</h1>
          <p className="alarms-page__subtitle">{t('Real-time panic and welfare alerts from guards on site')}</p>
        </div>

        <div className="alarms-page__stat-row">
          <div className={`alarms-stat-card ${openCount > 0 ? 'alarms-stat-card--open' : 'alarms-stat-card--zero'}`}>
            <FiAlertTriangle className="alarms-stat-card__icon" />
            <div>
              <div className="alarms-stat-card__value">{openCount}</div>
              <div className="alarms-stat-card__label">{t('Open')}</div>
            </div>
          </div>

          <div className={`alarms-stat-card ${ackedCount > 0 ? 'alarms-stat-card--acked' : 'alarms-stat-card--zero'}`}>
            <FiClock className="alarms-stat-card__icon" />
            <div>
              <div className="alarms-stat-card__value">{ackedCount}</div>
              <div className="alarms-stat-card__label">{t('In Progress')}</div>
            </div>
          </div>

          <div className={`alarms-stat-card ${totalActive === 0 ? 'alarms-stat-card--clear' : 'alarms-stat-card--zero'}`}>
            <FiCheckCircle className="alarms-stat-card__icon" />
            <div>
              <div className="alarms-stat-card__value">{totalActive === 0 ? '✓' : totalActive}</div>
              <div className="alarms-stat-card__label">{totalActive === 0 ? t('All Clear') : t('Total Active')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="alarms-page__toolbar">
        <div className="alarms-search-wrap">
          <FiSearch className="alarms-search-icon" />
          <input
            type="search"
            className="alarms-search"
            placeholder={t('Search by title, guard or site…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="alarms-search-clear"
              onClick={() => setSearch('')}
              title={t('Clear search')}
            >
              <FiX />
            </button>
          )}
        </div>

        {tab === 'active' && (
          <div className="alarms-filter-pills">
            {(['all', 'open', 'acknowledged'] as const).map((s) => (
              <button
                key={s}
                className={`alarms-filter-pill ${filterStatus === s ? 'alarms-filter-pill--active' : ''}`}
                onClick={() => setFilterStatus(s)}
              >
                {s === 'all' ? t('All||alarms') : t(statusConfig[s].label)}
                {s === 'open' && openCount > 0 && (
                  <span className="alarms-filter-pill__count alarms-filter-pill__count--red">
                    {openCount}
                  </span>
                )}
                {s === 'acknowledged' && ackedCount > 0 && (
                  <span className="alarms-filter-pill__count alarms-filter-pill__count--amber">
                    {ackedCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {tab === 'history' && (
          <div className="alarms-filter-pills">
            {(['all', 'resolved', 'cancelled'] as const).map((s) => (
              <button
                key={s}
                className={`alarms-filter-pill ${historyStatusFilter === s ? 'alarms-filter-pill--active' : ''}`}
                onClick={() => setHistoryStatusFilter(s)}
              >
                {s === 'all' ? t('All||alarms') : t(statusConfig[s].label)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div className="alarms-page__content">
        {tab === 'active' && (
          <>
            {filteredActive.length === 0 ? (
              <div className="alarms-empty">
                <FiCheckCircle className="alarms-empty__icon" />
                <div className="alarms-empty__title">
                  {search || filterStatus !== 'all'
                    ? t('No alarms match your filters')
                    : t('No active alarms')}
                </div>
                <div className="alarms-empty__sub">
                  {search || filterStatus !== 'all'
                    ? t('Try adjusting your search or filters')
                    : t('All guards are reporting safe')}
                </div>
              </div>
            ) : (
              <>
                {openAlarms.length > 0 && (
                  <div className="alarms-group">
                    <div className="alarms-group__header alarms-group__header--open">
                      <FiAlertTriangle className="alarms-group__icon" />
                      <span className="alarms-group__label">{t('Needs Response')}</span>
                      <span className="alarms-group__badge alarms-group__badge--red">
                        {openAlarms.length}
                      </span>
                    </div>
                    <div className="alarms-list">
                      {openAlarms.map((alarm) => (
                        <AlarmRow key={alarm.id} alarm={alarm} isFocused={alarm.id === focusedAlarmId} {...alarmRowProps} />
                      ))}
                    </div>
                  </div>
                )}

                {ackedAlarms.length > 0 && (
                  <div className="alarms-group">
                    <div className="alarms-group__header alarms-group__header--acked">
                      <FiClock className="alarms-group__icon" />
                      <span className="alarms-group__label">{t('In Progress')}</span>
                      <span className="alarms-group__badge alarms-group__badge--amber">
                        {ackedAlarms.length}
                      </span>
                    </div>
                    <div className="alarms-list">
                      {ackedAlarms.map((alarm) => (
                        <AlarmRow key={alarm.id} alarm={alarm} isFocused={alarm.id === focusedAlarmId} {...alarmRowProps} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {tab === 'history' && (
          <>
            {historyLoading ? (
              <div className="loading">
                <div className="spinner spinner--sm" />
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="alarms-empty">
                <FiCheckCircle className="alarms-empty__icon" />
                <div className="alarms-empty__title">
                  {search || historyStatusFilter !== 'all'
                    ? t('No alarms match your filters')
                    : t('No alarm history')}
                </div>
                <div className="alarms-empty__sub">
                  {search || historyStatusFilter !== 'all'
                    ? t('Try adjusting your search or filters')
                    : t('Resolved alarms will appear here')}
                </div>
              </div>
            ) : (
              <>
                <div className="alarms-result-bar">
                  <span className="alarms-result-count">
                    {filteredHistory.length === history.length
                      ? tn('{count} alarm', '{count} alarms', history.length)
                      : t('{shown} of {total} alarms', { shown: filteredHistory.length, total: history.length })}
                  </span>
                  {(search || historyStatusFilter !== 'all') && (
                    <button
                      className="alarms-result-clear"
                      onClick={() => { setSearch(''); setHistoryStatusFilter('all'); }}
                    >
                      <FiX /> {t('Clear filters')}
                    </button>
                  )}
                </div>

                <div className="alarms-table-wrap">
                  <table className="table alarms-table">
                    <thead>
                      <tr>
                        <SortableHeader col="status"    label="Status"    active={sortCol === 'status'}    dir={sortDir} onSort={handleSort} />
                        <SortableHeader col="title"     label="Alarm"     active={sortCol === 'title'}     dir={sortDir} onSort={handleSort} />
                        <SortableHeader col="guard"     label="Guard"     active={sortCol === 'guard'}     dir={sortDir} onSort={handleSort} />
                        <SortableHeader col="site"      label="Site"      active={sortCol === 'site'}      dir={sortDir} onSort={handleSort} />
                        <SortableHeader col="triggered" label="Triggered" active={sortCol === 'triggered'} dir={sortDir} onSort={handleSort} />
                        <SortableHeader col="duration"  label="Duration"  active={sortCol === 'duration'}  dir={sortDir} onSort={handleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((alarm) => {
                        const reportId = onOpenReport ? alarm.report_id ?? null : null;
                        return (
                          <tr
                            key={alarm.id}
                            className={`alarms-table__row ${reportId ? 'alarms-table__row--clickable' : ''}`}
                            onClick={reportId ? () => onOpenReport!(reportId) : undefined}
                            onKeyDown={
                              reportId
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      onOpenReport!(reportId);
                                    }
                                  }
                                : undefined
                            }
                            tabIndex={reportId ? 0 : undefined}
                            role={reportId ? 'button' : undefined}
                            title={reportId ? t('Open incident report') : undefined}
                          >
                            <td>
                              <StatusChip status={alarm.status} />
                            </td>
                            <td>
                              <div className="alarms-table__title">{alarm.title ?? '—'}</div>
                              {alarm.message && (
                                <div className="alarms-table__message">{alarm.message}</div>
                              )}
                            </td>
                            <td className="alarms-table__guard">
                              {alarm.profiles?.full_name ?? '—'}
                            </td>
                            <td className="alarms-table__site">{alarm.sites?.name ?? '—'}</td>
                            <td className="alarms-table__time">
                              {formatDatetime(alarm.triggered_at)}
                            </td>
                            <td className="alarms-table__duration">
                              {formatDuration(alarm.triggered_at, alarm.resolved_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
